/**
 * 게시판 리치 에디터 — 사용자에게 보이는 문구 한 곳 (SSOT).
 *
 * ⛔ **사실대로 말한다.** 붙어 있지 않은 것을 「처리 중」이라고 하면 안 된다 — 그 화면은
 *   영원히 끝나지 않고, 그것은 거짓이다(같은 원칙이 `apps/chat-server/src/modules/voice/
 *   voice.routes.ts` 의 주석에 적혀 있다). 그래서 그림 올리기는 세 상태를 **모두** 가진다:
 *
 *   1. 올리는 중 → 자리표시 + 「그림을 올리는 중입니다」
 *   2. 실패      → **지워지지 않는** 표시. 빈 곳으로 두지 않고, 바깥 주소도 남기지 않는다.
 *   3. 저장할 때 → 「올리지 못한 그림이 n개 있습니다」 — 사용자가 **무엇을 잃었는지 안다.**
 *
 * 문구를 고칠 때는 이 파일만 고친다. 화면·훅·시험이 모두 여기를 부른다.
 */

import { getNumberLocale } from '../../lib/base-i18n';

/** 1. 올리는 중 — 자리표시 옆에 붙는 말. */
export const IMAGE_UPLOADING = '그림을 올리는 중입니다';

/**
 * 2. 실패 — 지워지지 않는 표시.
 *
 * 괄호 안은 «까닭»이다. 까닭을 모를 때만 기본값(주소를 불러올 수 없음)을 쓴다.
 */
export const IMAGE_FAILED_REASON_DEFAULT = '주소를 불러올 수 없음';

/** 바깥(우리 것이 아닌) 주소의 그림 — 이 판에는 서버 프록시가 없다. */
export const IMAGE_FAILED_REASON_REMOTE = '바깥 주소의 그림은 아직 가져올 수 없음';

/** 그림이 아닌 것 · 해독할 수 없는 자료. */
export const IMAGE_FAILED_REASON_UNDECODABLE = '그림 자료를 읽을 수 없음';

export function imageFailedMessage(reason: string = IMAGE_FAILED_REASON_DEFAULT): string {
  return `이 그림은 올리지 못했습니다. (${reason}) — 다시 시도하거나 지워 주세요.`;
}

/** 3. 저장할 때 — 올리지 못한 그림이 남아 있음. */
export function saveBlockedByFailedImagesMessage(count: number): string {
  return `올리지 못한 그림이 ${count}개 있습니다`;
}

// ── 남은 길이 ───────────────────────────────────────────────────────────────
//
// ⚠ 세는 것은 «글자»가 아니라 «저장되는 HTML 의 길이»다. 표가 있으면 글자 수보다 훨씬
//   빨리 찬다(백엔드 실측: 표 25.70배). 그래서 문구가 **그 차이를 말해야** 한다 —
//   그러지 않으면 「8,000자인데 왜 막히지」가 된다.
// ⛔ 못 세면 이 문구를 쓰지 않는다. 그 자리를 «비운다»(content-length.ts 가 null 을 준다).

/**
 * ⚠ 로케일을 **박지 않는다** — `getNumberLocale()` 이 SSOT 다(`lib/base-i18n.ts` 의 `getNumberLocale`).
 *   처음에 로케일을 글자로 박아 적었더니 기존 가드가 잡았다
 *   (`lib/i18n-locale-hardcode.test.ts` — Cycle 55. ja·en 사용자에게 잘못 표시된다).
 *   ⚠ 그 가드는 **주석을 걷어내지 않는다** — 여기에 그 꼴을 «예시로도» 적으면 다시 걸린다.
 */
function num(n: number): string {
  return n.toLocaleString(getNumberLocale());
}

/** 늘 보이는 짧은 표시. 「글자 수」가 아니라는 것을 이름으로 말한다. */
export function contentLengthLabel(used: number, limit: number): string {
  return `저장 길이 ${num(used)} / ${num(limit)}`;
}

/** 90% 를 넘었을 때 — 넘기 «전»에 알린다. */
export function contentLengthNearLimitMessage(used: number, limit: number): string {
  return `상한에 가까워지고 있습니다 (${contentLengthLabel(used, limit)}). `
    + '세는 것은 글자 수가 아니라 서식·표를 포함한 저장 길이입니다 — 표가 있으면 빨리 찹니다.';
}

/** 이미 넘었을 때 — 지금 저장하면 서버가 거부한다는 «사실»을 말한다. */
export function contentLengthOverLimitMessage(used: number, limit: number): string {
  return `상한을 넘었습니다 (${contentLengthLabel(used, limit)}). `
    + `${num(used - limit)}만큼 줄여야 저장됩니다 — 표를 줄이면 가장 많이 줄어듭니다.`;
}

/**
 * 서버가 400 으로 거부했을 때 — **한국어로** 말한다.
 *
 * 서버 원문은 영어이고 「지금 몇 자인지」가 없다:
 *   `body/content must NOT have more than 50000 characters`
 * 그것을 그대로 보여 주면 사용자가 무엇을 해야 할지 모른다. 상한은 서버가 말한 값을 쓰고
 * (우리 상수보다 그쪽이 정본이다), 지금 길이는 우리가 재서 함께 말한다.
 *
 * ⛔ 길이를 못 재면 그 자리를 «비운다» — 지어내지 않는다.
 */
export function savedTooLongByServerMessage(limit: number, used?: number | null): string {
  const head = `본문이 너무 길어 저장하지 못했습니다. 상한은 ${num(limit)} 입니다`;
  if (typeof used !== 'number' || !Number.isFinite(used)) {
    return `${head}. 표를 줄이면 가장 많이 줄어듭니다.`;
  }
  return `${head} (${contentLengthLabel(used, limit)}) — ${num(Math.max(0, used - limit))}만큼 줄여 주세요. `
    + '표를 줄이면 가장 많이 줄어듭니다.';
}
